import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Pencil,
  Search,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { ShellContext } from '@/components/layout/AppShell';
import { BillingLedger } from '@/components/billing/Ledger';
import { Board } from '@/components/tasks/Board';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { billingApi, projectsApi } from '@/lib/api/endpoints';
import type { Client, Project, UnfiledStats } from '@/lib/api/types';
import { describeFailure } from '@/lib/i18n/errors';
import { useT } from '@/lib/i18n';
import { formatAmount, formatMinutes } from '@/routes/Clients';
import { Page } from '@/routes/Today';
import { cn } from '@/lib/utils';

/**
 * Projects — what a body of work gathers, and what it adds up to.
 *
 * Every row answers the four questions a label never could: how many tasks
 * are left, how many hours went in, how much is waiting to be billed, and
 * whether it is late. The numbers come from the server, computed the same way
 * issuing a report computes them.
 */
export function ProjectsRoute() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['projects', showArchived],
    queryFn: () => projectsApi.list(showArchived),
  });
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => billingApi.clients(),
  });
  // Work with no project still happened. Without this row the screen is a
  // partial account: hours exist that no card explains.
  const { data: unfiled } = useQuery({
    queryKey: ['projects', 'unfiled'],
    queryFn: () => projectsApi.unfiled(),
  });

  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => projectsApi.create({ name }),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const folded = fold(search);
  const visible = (data ?? []).filter((project) => {
    if (!folded) return true;
    const client = clients?.find((row) => row.id === project.client_id);
    return (
      fold(project.name).includes(folded) ||
      (client ? fold(client.name).includes(folded) : false)
    );
  });

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );

  return (
    <Page>
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">{t('projects.title')}</h1>
        {/* The archive is a filter, not another screen: an archived project is
            the same project, and the way back must be where it went. */}
        <Button variant="ghost" size="sm" onClick={() => setShowArchived((on) => !on)}>
          <Archive className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {showArchived ? t('projects.hideArchived') : t('projects.showArchived')}
        </Button>
      </div>

      <form
        className="mb-6 flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('projects.namePlaceholder')}
          className="min-w-48 flex-1 rounded-lg bg-surface px-3 py-2 text-sm ring-1 ring-border focus:outline-none focus:ring-accent text-content placeholder:text-content-subtle"
        />
        <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
          {t('projects.add')}
        </Button>
      </form>

      {/* Search is a filter over the page, not a round trip: the list is a
          handful of names, and a request per keystroke would be slower than
          reading them. Matches the client's name too — "everything for
          Xiaomi" is how a project is looked for as often as by its own name. */}
      {data.length > 4 || search ? (
        <div className="relative mb-4">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-subtle"
            aria-hidden
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('projects.search')}
            className="w-full rounded-lg bg-surface py-2 pl-9 pr-3 text-sm ring-1 ring-border focus:outline-none focus:ring-accent text-content placeholder:text-content-subtle"
          />
        </div>
      ) : null}

      {visible.length === 0 ? (
        <EmptyState
          icon={<FolderKanban className="h-5 w-5" />}
          title={search ? t('projects.noMatch') : t('projects.empty.title')}
          hint={search ? t('projects.noMatchHint') : t('projects.empty.hint')}
        />
      ) : (
        <ul className="space-y-2">
          {visible.map((project) => (
            <ProjectCard key={project.id} project={project} clients={clients ?? []} />
          ))}
        </ul>
      )}

      {/* Last, and it cannot be opened or edited: "no project" is the absence
          of a row, shown only so the totals add up. */}
      {!search &&
      !showArchived &&
      unfiled &&
      unfiled.open_tasks + unfiled.done_tasks > 0 ? (
        <UnfiledCard stats={unfiled} />
      ) : null}
    </Page>
  );
}

function UnfiledCard({ stats }: { stats: UnfiledStats }) {
  const t = useT();
  const total = stats.open_tasks + stats.done_tasks;
  const progress = total === 0 ? 0 : Math.round((stats.done_tasks / total) * 100);
  return (
    <section className="mt-2 rounded-xl border border-dashed border-border px-4 py-3">
      <div className="mb-3">
        <h2 className="text-sm font-medium text-content-muted">
          {t('projects.unfiled')}
        </h2>
        <p className="text-xs text-content-subtle">{t('projects.unfiledHint')}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t('projects.tasks')} value={String(stats.open_tasks)} />
        <Stat
          label={t('projects.tracked')}
          value={formatMinutes(stats.tracked_minutes)}
        />
        <Stat
          label={t('projects.unbilled')}
          value={
            stats.unbilled_by_currency.length > 0
              ? stats.unbilled_by_currency
                  .map((row) => formatAmount(Number(row.amount), row.currency))
                  .join(' + ')
              : '—'
          }
          tone={stats.unbilled_by_currency.length > 0 ? 'accent' : 'plain'}
        />
        <Stat label={t('projects.progress')} value={`${progress} %`} />
      </div>
    </section>
  );
}

function ProjectCard({ project, clients }: { project: Project; clients: Client[] }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [showsBilling, setShowsBilling] = useState(false);
  const [showsBoard, setShowsBoard] = useState(false);
  const { openTask } = useOutletContext<ShellContext>();
  const update = useMutation({
    mutationFn: (patch: Partial<Project> & { archived?: boolean }) =>
      projectsApi.update(project.id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });
  const remove = useMutation({
    mutationFn: () => projectsApi.remove(project.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['projects'] }),
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const client = clients.find((row) => row.id === project.client_id);
  // The project's own currency, which the server also sends — a project whose
  // client was archived off the list still prints its amounts.
  const currency = client?.currency ?? project.currency ?? null;
  const total = project.open_tasks + project.done_tasks;
  // Ordered by what it asks of you: a debt first, then work to invoice, then
  // money already in — and a zero says nothing at all.
  const money_chips = (
    [
      {
        label: t('projects.owed'),
        amount: project.billed_unpaid_amount,
        tone: 'danger',
      },
      {
        label: t('projects.unbilled'),
        amount: project.unbilled_amount,
        tone: 'accent',
      },
      { label: t('projects.paid'), amount: project.paid_amount, tone: 'plain' },
    ] as const
  )
    .filter((row) => Number(row.amount) > 0)
    .map((row) => ({ ...row, value: money(row.amount, currency) }));
  const progress = total === 0 ? 0 : Math.round((project.done_tasks / total) * 100);
  const archived = Boolean(project.archived_at);

  return (
    <li
      className={cn(
        'rounded-xl bg-surface p-4 ring-1 ring-border',
        archived && 'opacity-60',
      )}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: `var(--color-label-${project.color})` }}
          aria-hidden
        />
        <span className="text-sm font-medium text-balance">{project.name}</span>
        {/* Who it is for. This was visible only while editing — the one moment
            you already know. */}
        {client ? (
          <span className="truncate text-xs text-content-subtle">{client.name}</span>
        ) : null}
        {archived ? (
          <span className="text-xs text-content-subtle">{t('clients.archived')}</span>
        ) : project.days_late !== null ? (
          <span className="rounded-full bg-danger-subtle px-2 py-0.5 text-xs font-medium text-danger">
            {t('projects.late', { count: project.days_late })}
          </span>
        ) : project.due_date ? (
          <span className="text-xs text-content-subtle">
            {formatDay(project.due_date)}
          </span>
        ) : null}
        {/* Late work asks for attention on the line you read first, not from
            inside a grid of figures that are mostly zero. */}
        {project.overdue_tasks > 0 ? (
          <span className="rounded-full bg-danger-subtle px-2 py-0.5 text-xs font-medium tabular-nums text-danger">
            {t('projects.overdue', { count: project.overdue_tasks })}
          </span>
        ) : null}
        <span className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          title={t('projects.edit')}
          aria-label={t('projects.edit')}
          onClick={() => setEditing((open) => !open)}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
        </Button>
        {/* Spelled out when archived: getting a project back is the one
            thing somebody looking at the archive came to do, and a bare icon
            is a poor place to hide it. */}
        {archived ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => update.mutate({ archived: false })}
          >
            <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            {t('clients.unarchive')}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            title={t('clients.archive')}
            aria-label={t('clients.archive')}
            onClick={() => update.mutate({ archived: true })}
          >
            <Archive className="h-3.5 w-3.5" aria-hidden />
          </Button>
        )}
        {total === 0 ? (
          <Button
            variant="ghost"
            size="sm"
            title={t('projects.delete')}
            onClick={() => remove.mutate()}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className="mb-4 grid gap-3 rounded-lg bg-canvas p-3 sm:grid-cols-2">
          <Labeled label={t('projects.namePlaceholder')}>
            <input
              defaultValue={project.name}
              onBlur={(event) => {
                if (event.target.value.trim() && event.target.value !== project.name) {
                  update.mutate({ name: event.target.value.trim() });
                }
              }}
              className="w-full rounded-lg bg-surface px-2 py-1.5 text-sm ring-1 ring-border focus:outline-none focus:ring-accent text-content placeholder:text-content-subtle"
            />
          </Labeled>
          <Labeled label={t('clients.client')}>
            <select
              value={project.client_id ?? ''}
              onChange={(event) =>
                update.mutate({ client_id: event.target.value || null })
              }
              className="w-full rounded-lg bg-surface px-2 py-1.5 text-sm ring-1 ring-border focus:outline-none text-content placeholder:text-content-subtle"
            >
              <option value="">{t('clients.none')}</option>
              {clients.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </Labeled>
          <Labeled label={t('projects.due')}>
            <input
              type="date"
              defaultValue={project.due_date ?? ''}
              onChange={(event) =>
                update.mutate({ due_date: event.target.value || null })
              }
              className="w-full rounded-lg bg-surface px-2 py-1.5 text-sm ring-1 ring-border focus:outline-none text-content placeholder:text-content-subtle"
            />
          </Labeled>
          <Labeled label={t('clients.rate')}>
            <input
              defaultValue={project.hourly_rate ?? ''}
              placeholder={
                client
                  ? formatAmount(Number(client.hourly_rate ?? 0), client.currency)
                  : t('clients.rate')
              }
              onBlur={(event) => {
                const raw = event.target.value.replace(',', '.').trim();
                update.mutate({ hourly_rate: raw || null });
              }}
              inputMode="decimal"
              className="w-full rounded-lg bg-surface px-2 py-1.5 text-sm ring-1 ring-border focus:outline-none text-content placeholder:text-content-subtle"
            />
          </Labeled>
          <Labeled label={t('projects.color')}>
            <div className="flex flex-wrap gap-1.5">
              {PROJECT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={color}
                  onClick={() => update.mutate({ color })}
                  className={cn(
                    'h-5 w-5 rounded-full ring-offset-2 transition-surface',
                    project.color === color && 'ring-2 ring-accent',
                  )}
                  style={{ backgroundColor: `var(--color-label-${color})` }}
                />
              ))}
            </div>
          </Labeled>
        </div>
      ) : null}

      {/* One line for the work. The bar, the count and the percentage were
          three drawings of a single fact, each taking a third of a grid; now
          the bar carries it and the numbers sit beside it.
          The bar is capped rather than stretched: at full card width the count
          drifted to the far edge and stopped reading as its label. A project
          with no tasks yet says nothing here at all — "0/0" beside an empty
          bar is a row that exists only to be ignored. */}
      {total > 0 ? (
        <div className="flex items-center gap-2.5">
          <div className="h-1.5 w-full max-w-56 overflow-hidden rounded-full bg-canvas">
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="shrink-0 text-xs tabular-nums text-content-muted">
            {project.done_tasks}/{total}
          </span>
          {project.tracked_minutes > 0 ? (
            <span className="shrink-0 text-xs tabular-nums text-content-subtle">
              {formatMinutes(project.tracked_minutes)}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Money, but only what is true. Three labelled figures meant most cards
          spent half their height saying "0,00 Kč" three times; a project with
          nothing outstanding should say nothing, and the eye should land on
          the one that wants doing something about. */}
      {money_chips.length > 0 ? (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {money_chips.map((chip) => (
            <span
              key={chip.label}
              className={cn(
                'rounded-md px-2 py-1 text-xs tabular-nums',
                chip.tone === 'danger' && 'bg-danger-subtle text-danger',
                chip.tone === 'accent' && 'bg-accent/10 text-accent',
                chip.tone === 'plain' && 'bg-canvas text-content-muted',
              )}
            >
              <span className="mr-1.5 opacity-70">{chip.label}</span>
              {chip.value}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-1">
        <Expander
          open={showsBoard}
          onToggle={() => setShowsBoard((open) => !open)}
          label={t('board.title')}
        />
        <Expander
          open={showsBilling}
          onToggle={() => setShowsBilling((open) => !open)}
          label={showsBilling ? t('projects.hideBilling') : t('projects.showBilling')}
        />
      </div>

      {showsBoard ? (
        <div className="mt-2">
          <Board projectId={project.id} onOpenTask={openTask} />
        </div>
      ) : null}

      {showsBilling ? (
        <div className="mt-2">
          <BillingLedger projectId={project.id} currency={currency ?? undefined} />
        </div>
      ) : null}
    </li>
  );
}

/** The card's two drawers, opened the same way so they read as a pair. */
function Expander({
  open,
  onToggle,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1.5 rounded px-1 py-1 text-xs text-content-muted transition-surface hover:bg-surface-hover hover:text-content max-sm:min-h-10 touch:min-h-10"
    >
      {open ? (
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
      )}
      {label}
    </button>
  );
}

/** Amounts print with the project's currency, or a dash when there is no client. */
function money(amount: string, currency: string | null): string {
  return currency ? formatAmount(Number(amount), currency) : '—';
}

/** The fixed palette labels use — theme tokens, legible in both themes. */
const PROJECT_COLORS = [
  'slate',
  'red',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
] as const;

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-content-subtle">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Stat({
  label,
  value,
  note,
  tone = 'plain',
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'plain' | 'accent' | 'danger';
}) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</p>
      <p
        className={cn(
          'text-sm font-medium tabular-nums',
          tone === 'accent' && 'text-accent',
          tone === 'danger' && 'text-danger',
        )}
      >
        {value}
      </p>
      {note ? <p className="text-[11px] text-danger">{note}</p> : null}
    </div>
  );
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day}. ${month}. ${year}`;
}

/** Lowercase and strip diacritics, so "chata" finds "Chatě". */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}
